/**
 * Contestant Field Visibility Utility
 *
 * Determines which contestant data fields are visible to each role.
 * Exported for use in AdminParticipantsPage and property-based tests.
 *
 * Requirements: 3.7, 4.4, 4.9, 5.3, 5.4, 5.5
 */

import type { RoleType } from '@/context/AuthContext';

/**
 * Categories of contestant data fields.
 *
 * - phase1: Phase 1 application data (personal info, background, etc.)
 * - phase2: Phase 2 competition data (Stage Form, Interview Info, Placement Questions)
 * - marriageInfo: Marriage-related fields (spouse name, marriage date)
 * - socialMedia: Social media handle
 * - actions: Action buttons (approve, reject, lock/unlock, etc.)
 */
export type ContestantFieldCategory =
  | 'phase1'
  | 'phase2'
  | 'marriageInfo'
  | 'socialMedia'
  | 'actions';

/**
 * Returns the set of visible contestant field categories for a given role.
 *
 * - Admin: all categories (phase1, phase2, marriageInfo, socialMedia, actions)
 * - Power User: phase1 only, no actions (no approve/reject)
 * - Judge: phase2 + marriageInfo + socialMedia, read-only (no actions)
 * - Contestant: no access (empty set — contestants don't see this page)
 */
export function getVisibleContestantFields(role: RoleType): Set<ContestantFieldCategory> {
  switch (role) {
    case 'admin':
      return new Set<ContestantFieldCategory>(['phase1', 'phase2', 'marriageInfo', 'socialMedia', 'actions']);
    case 'poweruser':
      return new Set<ContestantFieldCategory>(['phase1']);
    case 'judge':
      return new Set<ContestantFieldCategory>(['phase2', 'marriageInfo', 'socialMedia']);
    case 'contestant':
    default:
      return new Set<ContestantFieldCategory>([]);
  }
}

/**
 * Convenience helpers derived from getVisibleContestantFields.
 */
export function canSeePhase1(role: RoleType): boolean {
  return getVisibleContestantFields(role).has('phase1');
}

export function canSeePhase2(role: RoleType): boolean {
  return getVisibleContestantFields(role).has('phase2');
}

export function canSeeMarriageInfo(role: RoleType): boolean {
  return getVisibleContestantFields(role).has('marriageInfo');
}

export function canSeeSocialMedia(role: RoleType): boolean {
  return getVisibleContestantFields(role).has('socialMedia');
}

export function canPerformActions(role: RoleType): boolean {
  return getVisibleContestantFields(role).has('actions');
}
